/**
 * funnel-variants.js
 * Variant picker logic for the bungee-funnel landing page.
 * Reads product data from window.funnelProductData (injected by funnel-hero.liquid).
 *
 * KEY FEATURE: Color swatch → hero image swap uses the exact CDN URLs
 * from the Claude design's primitives.jsx / standalone-src.html resource map.
 * Falls back to variant.featured_image if Shopify has per-variant images set.
 */
(function () {
  'use strict';

  // ─── Exact CDN image URLs from Claude design primitives.jsx ───────────────
  // Source: standalone-src.html __LR_URL_MAP + primitives.jsx LR_IMG
  var COLOR_IMG_MAP = {
    blue: 'https://leapsandrebounds.com/cdn/shop/files/blue_fitness_rebounder_leaps_and_rebounds_65141d67-08ce-4c45-973f-5c629bcc4ca4.webp?height=900&v=1764743413',
    green:
      'https://leapsandrebounds.com/cdn/shop/files/green_fitness_rebounder_leaps_and_rebounds.webp?height=900&v=1764743413',
    red: 'https://leapsandrebounds.com/cdn/shop/files/red_fitness_rebounder_leaps_and_rebounds.webp?height=900&v=1764743413',
    orange:
      'https://leapsandrebounds.com/cdn/shop/files/orange_fitness_rebounder_leaps_and_rebounds.webp?height=900&v=1764743413',
    grey: 'https://leapsandrebounds.com/cdn/shop/files/grey_fitness_rebounder_leaps_and_rebounds.webp?height=900&v=1764743413',
    gray: 'https://leapsandrebounds.com/cdn/shop/files/grey_fitness_rebounder_leaps_and_rebounds.webp?height=900&v=1764743413',
    pink: 'https://leapsandrebounds.com/cdn/shop/files/pink_fitness_rebounder_leaps_and_rebounds.webp?height=900&v=1764743413',
    purple:
      'https://leapsandrebounds.com/cdn/shop/files/purple_fitness_rebounder_leaps_and_rebounds.webp?height=900&v=1764743413',
  };

  // ─── Swatch hex colors (JS-applied — beats any CSS/inline style) ──────────
  var COLOR_HEX_MAP = {
    blue: '#2271b3',
    green: '#4a6b34',
    red: '#8b2020',
    orange: '#e87722',
    grey: '#636363',
    gray: '#636363',
    pink: '#c97490',
    purple: '#6b3fa0',
    black: '#1a1a1a',
    white: '#f5f5f0',
    teal: '#2a9d8f',
    yellow: '#f4c430',
    navy: '#1b3a6b',
  };

  // ─── Paint swatch colors imperatively ─────────────────────────────────────
  // Runs immediately on DOMContentLoaded so colors show before any interaction.
  // JS el.style.background is the highest-priority mechanism — cannot be
  // overridden by any theme or page CSS without !important.
  function paintSwatches() {
    document.querySelectorAll('[data-funnel-swatch]').forEach(function (btn) {
      var key = (btn.dataset.value || '').toLowerCase().trim();
      var hex = COLOR_HEX_MAP[key];
      if (hex) {
        btn.style.setProperty('background', hex, 'important');
      }
    });
  }

  function init() {
    // Paint swatch colors immediately — before any variant logic runs.
    // This is the nuclear fallback: sets background via style.setProperty('background', hex, 'important')
    // which beats every CSS rule including theme stylesheets.
    paintSwatches();

    var data = window.funnelProductData;
    if (!data || !data.variants) return;

    var variants = data.variants;

    // Pre-index variants by option string for O(1) lookup
    var variantMap = {};
    variants.forEach(function (v) {
      var key = v.options
        .map(function (o) {
          return o.toLowerCase();
        })
        .join('|');
      variantMap[key] = v;
    });

    // Track selected options (seeded from selectedVariant)
    var selected = {};
    if (data.selectedVariant) {
      data.selectedVariant.options.forEach(function (val, i) {
        selected[i] = val.toLowerCase();
      });
    }

    function lookupVariant() {
      var key = Object.keys(selected)
        .sort()
        .map(function (i) {
          return selected[i];
        })
        .join('|');
      return variantMap[key] || null;
    }

    // ── Hero image update ────────────────────────────────────────────────────
    function updateHeroImage(colorName, variantFeaturedImage) {
      var heroImg = document.querySelector('[data-funnel-hero-img]');
      var heroVid = document.querySelector(
        '[data-funnel-hero-img] video, video[data-funnel-hero-img]'
      );
      if (!heroImg && !heroVid) return;

      // Priority: 1) exact CDN map by color name  2) variant.featured_image  3) keep current
      var colorKey = (colorName || '').toLowerCase();
      var newSrc =
        COLOR_IMG_MAP[colorKey] || (variantFeaturedImage && variantFeaturedImage.src) || null;

      if (!newSrc) return;

      if (heroVid) {
        // Update video poster (can't swap the src without reloading)
        heroVid.poster = newSrc;
      } else if (heroImg.tagName === 'VIDEO') {
        heroImg.poster = newSrc;
      } else if (heroImg.style !== undefined) {
        // It's a background-image div
        heroImg.style.backgroundImage = "url('" + newSrc + "')";
      } else {
        heroImg.src = newSrc;
      }

      // Also update sticky cart thumb
      var stickyThumb = document.getElementById(
        'funnel-sticky-thumb-' + (window.funnelStickyId || '')
      );
      // Fallback: query any sticky thumb
      if (!stickyThumb) stickyThumb = document.querySelector('.sticky-cta .thumb');
      if (stickyThumb) stickyThumb.style.backgroundImage = "url('" + newSrc + "')";
    }

    // skipAvailability = true on first render so we never overwrite what
    // Liquid already rendered (server-side knows the real stock state).
    // Only enforce availability when the user actively changes a swatch/pill.
    function updateUI(variant, colorName, skipAvailability) {
      if (!variant) return;

      // Persist to global state
      window.funnelState = window.funnelState || {};
      window.funnelState.variantId = variant.id;
      window.funnelState.price = variant.price;
      window.funnelState.compareAtPrice = variant.compare_at_price;

      var fmt =
        window.funnelFormatMoney ||
        function (c) {
          return '$' + (c / 100).toFixed(0);
        };

      // Price display
      document.querySelectorAll('[data-funnel-price]').forEach(function (el) {
        var nowEl = el.querySelector('[data-price-now]');
        var wasEl = el.querySelector('[data-price-was]');
        var saveEl = el.querySelector('[data-price-save]');
        if (nowEl) nowEl.textContent = fmt(variant.price);
        if (wasEl) {
          wasEl.textContent =
            variant.compare_at_price > variant.price ? fmt(variant.compare_at_price) : '';
          wasEl.style.display = variant.compare_at_price > variant.price ? '' : 'none';
        }
        if (saveEl) {
          var saved = (variant.compare_at_price || 0) - variant.price;
          saveEl.textContent = saved > 0 ? 'SAVE ' + fmt(saved) : '';
          saveEl.style.display = saved > 0 ? '' : 'none';
        }
      });

      // Inline cart price labels (inside CTA buttons)
      document.querySelectorAll('[data-cart-price]').forEach(function (el) {
        el.textContent = fmt(variant.price);
      });

      // All hidden variant inputs (hero form + sticky form + final CTA form)
      document.querySelectorAll('[data-cart-variant]').forEach(function (el) {
        el.value = variant.id;
      });

      // Hero image swap — uses exact CDN map, falls back to variant image
      updateHeroImage(colorName, variant.featured_image);

      // URL param update
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // Availability — skip on initial render, Liquid already rendered correct state
      if (!skipAvailability) {
        document.querySelectorAll('[data-funnel-atc-btn]').forEach(function (btn) {
          if (variant.available) {
            btn.disabled = false;
            btn.classList.remove('is-sold-out');
            // Restore button text if it was sold-out before
            if (btn.dataset.defaultText) btn.textContent = btn.dataset.defaultText;
          } else {
            if (!btn.dataset.defaultText) btn.dataset.defaultText = btn.textContent;
            btn.disabled = true;
            btn.classList.add('is-sold-out');
            btn.textContent = 'SOLD OUT';
          }
        });
      }
    }

    // ── Wire color swatches ──────────────────────────────────────────────────
    // In redirect mode swatches are decorative only — no clicks wired.
    if (window.funnelAtcMode === 'redirect') {
      document.querySelectorAll('[data-funnel-swatch]').forEach(function (btn) {
        btn.style.cursor = 'default';
        btn.setAttribute('aria-disabled', 'true');
        btn.setAttribute('title', 'Choose your color on the product page');
      });
      document.querySelectorAll('[data-funnel-pill]').forEach(function (btn) {
        btn.style.cursor = 'default';
        btn.setAttribute('aria-disabled', 'true');
      });
    } else {
      document.querySelectorAll('[data-funnel-swatch]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var optionIndex = parseInt(btn.dataset.optionIndex, 10);
          var value = btn.dataset.value.toLowerCase();
          selected[optionIndex] = value;

          // Update picker label
          var group = btn.closest('[data-option-group]');
          var labelEl = group && group.querySelector('[data-selected-label="' + optionIndex + '"]');
          if (labelEl) labelEl.textContent = btn.dataset.value;

          // Toggle active swatch in group
          if (group) {
            group.querySelectorAll('[data-funnel-swatch]').forEach(function (b) {
              b.classList.toggle('is-active', b === btn);
            });
          }

          var variant = lookupVariant();
          // Color is whichever option has swatches — find it from selected state
          // by checking which key has a value that matches the COLOR_IMG_MAP
          var colorName = null;
          Object.keys(selected).forEach(function (idx) {
            if (COLOR_IMG_MAP[selected[idx]]) colorName = selected[idx];
          });
          colorName = colorName || value;
          updateUI(variant, colorName, false); // false = do enforce availability on user interaction
        });
      });

      // ── Wire size pills ──────────────────────────────────────────────────────
    } // end redirect-mode guard
    if (window.funnelAtcMode !== 'redirect') {
      document.querySelectorAll('[data-funnel-pill]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var optionIndex = parseInt(btn.dataset.optionIndex, 10);
          var value = btn.dataset.value.toLowerCase();
          selected[optionIndex] = value;

          // Update picker label
          var group = btn.closest('[data-option-group]');
          var labelEl = group && group.querySelector('[data-selected-label="' + optionIndex + '"]');
          if (labelEl) labelEl.textContent = btn.dataset.value;

          if (group) {
            group.querySelectorAll('[data-funnel-pill]').forEach(function (b) {
              b.classList.toggle('is-active', b === btn);
            });
          }

          var variant = lookupVariant();
          // Find current color from selected state
          var colorName = null;
          Object.keys(selected).forEach(function (idx) {
            if (COLOR_IMG_MAP[selected[idx]]) colorName = selected[idx];
          });
          updateUI(variant, colorName, false); // false = enforce availability
        });
      });
    } // end redirect-mode guard

    // ── Initial render — sync price/variant-id/image but DON'T touch ATC state
    // Liquid already rendered the correct in-stock state server-side.
    var initial = lookupVariant();
    var initColor = null;
    Object.keys(selected).forEach(function (idx) {
      if (COLOR_IMG_MAP[selected[idx]]) initColor = selected[idx];
    });
    initColor = initColor || 'blue';
    updateUI(initial, initColor, true); // true = skip availability check on init
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
